/**
 * Monitoring Points Manager
 * Handles monitoring points and polygons display and interaction
 * Also handles Season polygons with clustering
 */

import {Injectable, inject} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {MapMonitoringObjectService, MapMonitoringObject} from '@6grain/masofa-monolith-api-client/map';
import {Field, FieldService, Season, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {Observable, BehaviorSubject, Subject, forkJoin, of} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {FieldFilter} from '@6grain/masofa-monolith-api-client/common/model/fieldFilter';
import {FilterOperator} from '@app/utils/filter-operator';
import {CROP_COLOR_MAP} from '@app/utils/_crop-colors';
import {Crop, CropService} from '@6grain/masofa-monolith-api-client/dictionaries';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {CropCacheService} from '@services/caches/crop-cache.service';
import {escapePopupHtml} from '@app/pages/electronic-field-map/pages/gis-fields/managers/gis-popup-html.util';
import {formatSeasonName, seasonNameKindFrom} from '@app/utils/season-name.util';

export type DisplayMode = 'monitoring' | 'seasons';


@Injectable({providedIn: 'root'})
export class MonitoringPointsManager {
  private translate = inject(TranslateService);
  private mapMonitoringObjectService = inject(MapMonitoringObjectService);
  private seasonService = inject(SeasonService);
  private fieldService = inject(FieldService);
  private cropService = inject(CropService);
  private cropCacheService = inject(CropCacheService);
  private mapInstance: OpenStreetMapComponent | null = null;

  // Store data
  public monitoringObjects$ = new BehaviorSubject<MapMonitoringObject[]>([]);
  private seasons$ = new BehaviorSubject<Season[]>([]);
  public cropsForColor$ = new BehaviorSubject<Crop[]>([]);
  public crops$ = new BehaviorSubject<Crop[]>([]);

  // Display mode
  private displayMode$ = new BehaviorSubject<DisplayMode>('monitoring');

  // Zoom settings
  private currentZoom: number = 13;
  private zoomThreshold: number = 14; // Show markers/polygons at zoom >= 14

  // Track markers and polygons added by this manager
  private managedMarkerIds: Set<string> = new Set();
  private managedPolygonIds: Set<string | number> = new Set();

  /** When false, zoom/mode listeners must not re-draw cached monitoring data (General GIS layer toggle). */
  private mapRenderingEnabled = true;
  private zoomListenerBound = false;
  private displayModeListenerBound = false;

  // Map to store crop name by cropId for color lookup
  private cropNameMap: Map<string, string> = new Map();
  private fieldNameById: Map<string, string> = new Map();

  // Track last rendered monitoring view to avoid re-adding layers unnecessarily

  // Event emitters for clicks
  public markerClick$ = new Subject<{ lat: number, lng: number, data: any }>();
  public polygonClick$ = new Subject<{ lat: number, lng: number, data: any }>();

  /**
   * Initialize the manager with a map instance
   */
  initialize(mapInstance: OpenStreetMapComponent): void {
    this.mapInstance = mapInstance;
    if (!this.zoomListenerBound) {
      this.setupZoomListener();
      this.zoomListenerBound = true;
    }

    if (!this.displayModeListenerBound) {
      this.displayMode$.subscribe(() => {
        this.updateDisplay();
      });
      this.displayModeListenerBound = true;
    }
  }

  /** Suspend/resume map rendering without clearing cached API data. */
  setMapRenderingEnabled(enabled: boolean): void {
    this.mapRenderingEnabled = enabled;
    if (!enabled) {
      this.clearMonitoringLayers();
      return;
    }
    this.updateDisplay();
  }

  /**
   * Setup zoom listener to update rendering based on zoom level
   */
  private setupZoomListener(): void {
    if (this.mapInstance?.mapZoomChange$) {
      this.mapInstance.mapZoomChange$.subscribe((zoom: number) => {
        this.currentZoom = zoom;
        this.updateDisplay();
      });
    }
  }

  /**
   * Load monitoring objects from API
   */
  loadMonitoringObjects(
    regionId: string | undefined,
    options?: { preserveDisplayMode?: boolean }
  ): Observable<MapMonitoringObject[]> {
    if (!options?.preserveDisplayMode) {
      this.setDisplayMode('monitoring');
    }
    return this.mapMonitoringObjectService
      .tilesMapMonitoringObjectGetByRegionIdGet(regionId)
      .pipe(
        map((objects: MapMonitoringObject[]) => {
          this.monitoringObjects$.next(objects);
          if (this.displayMode$.value === 'monitoring') {
            this.updateDisplay();
          }

          // Load crops after monitoring objects are loaded for the first time
          this.loadCropsAfterMonitoringInit();

          return objects;
        })
      );
  }

  /**
   * Get crops observable
   */
  public getCrops$(): Observable<Crop[]> {
    return this.cropsForColor$.asObservable();
  }

  /**
   * Load seasons by cropId from API
   * @param cropId - The crop ID
   */
  loadSeasonsByCropId(cropId: string): Observable<Season[]> {
    // Clear all existing season polygons before loading new ones
    this.clearSeasonPolygons();

    // Look up crop name from existing crops
    const crops = this.cropsForColor$.value;
    const crop: any = crops.find(c => c.id === cropId);

    if (crop) {
      this.cropNameMap.set(cropId, crop.name as string);
    }

    const filters: FieldFilter[] = [{
      filterField: 'cropId',
      filterOperator: FilterOperator.Equals,
      filterValue: cropId
    }];
    return this.seasonService
      .cropMonitoringSeasonGetByQueryPost({filters: filters} as any)
      .pipe(
        switchMap((seasons: Season[]) =>
          forkJoin({
            seasons: of(seasons),
            crops: this.cropCacheService.getCrops(),
            fieldNames: this.loadFieldNamesByIds(this.uniqueFieldIds(seasons)),
          })
        ),
        tap(({seasons, fieldNames}) => {
          this.fieldNameById = fieldNames;
          this.seasons$.next(seasons);
          if (this.displayMode$.value === 'seasons') {
            this.updateDisplay();
          }
        }),
        map(({seasons}) => seasons)
      );
  }

  /**
   * Clear all season polygons from the map
   */
  private clearSeasonPolygons(): void {
    if (!this.mapInstance) return;

    // Remove only season polygons (those with 'season_' prefix in their ID)
    const seasonPolygonIds = Array.from(this.managedPolygonIds).filter(id =>
      typeof id === 'string' && id.toString().startsWith('season_')
    );

    seasonPolygonIds.forEach(polygonId => {
      this.mapInstance!.mapController$.next({removePolygon: {id: polygonId}});
      this.managedPolygonIds.delete(polygonId);
    });
  }

  /**
   * Load crops after regions and monitoring points
   * Creates an array of Crop objects with color mapping
   */
  loadCrops(cropsData: Crop[]): void {
    const crops = cropsData.map(crop => {
      // @ts-ignore
      const cropColor = CROP_COLOR_MAP[crop.names?.['en-US']] || '#4CAF50'; // Default green if not found
      return {
        id: crop.id,
        name: crop.names?.['en-US'],
        color: cropColor
      };
    });

    this.cropsForColor$.next(crops);

    // Update crop name map for quick lookup
    crops.forEach(crop => {
      this.cropNameMap.set(crop.id as string, crop.name as string);
    });
  }

  /**
   * Load crops from CropsService after monitoring objects initialization
   * Only loads crops if the crops array is empty
   * This should be called after loadMonitoringObjects completes
   */
  loadCropsAfterMonitoringInit(): void {
    // Check if crops are already loaded
    const currentCrops = this.cropsForColor$.value;
    if (currentCrops && currentCrops.length > 0) {
      return;
    }

    // Load crops from service
    this.cropService.dictionariesCropGetByQueryPost({}).subscribe({
      next: (cropsData: Crop[]) => {
        if (cropsData && cropsData.length > 0) {
          this.crops$.next(cropsData);
          // Process and store crops with color mapping
          this.loadCrops(cropsData);
        } else {
        }
      },
    });
  }

  /**
   * Update display based on current mode and zoom
   */
  private updateDisplay(): void {
    if (!this.mapInstance || !this.mapRenderingEnabled) {
      return;
    }

    if (this?.mapInstance?.weatherStationManager?.weatherStationMarkersCount === 0) {
      const mode = this.displayMode$.value;
      if (mode === 'monitoring') {
        this.updateMonitoringObjectsDisplay();
      } else if (mode === 'seasons') {
        this.updateSeasonsDisplay();
      }
    }
  }

  /**
   * Set display mode (monitoring or seasons)
   */
  setDisplayMode(mode: DisplayMode): void {
    const previousMode = this.displayMode$.value;

    // Only clear layers when actually switching modes
    if (previousMode !== mode) {
      if (mode === 'monitoring') {
        this.clearSeasonLayers();
      } else if (mode === 'seasons') {
        this.clearMonitoringLayers();
      }
    }

    this.displayMode$.next(mode);
  }

  /**
   * Clear monitoring layers (monitoring objects, markers and polygons)
   */
  clearMonitoringLayers(): void {
    if (!this.mapInstance) return;

    // Remove only monitoring markers (those with 'monitoring_' prefix)
    const monitoringMarkerIds = Array.from(this.managedMarkerIds).filter(id =>
      id.toString().startsWith('monitoring_')
    );

    monitoringMarkerIds.forEach(markerId => {
      this.mapInstance!.mapController$.next({removeMarker: {id: markerId}});
      this.managedMarkerIds.delete(markerId);
    });

    // Remove only monitoring polygons (those without 'season_' prefix)
    const monitoringPolygonIds = Array.from(this.managedPolygonIds).filter(id =>
      typeof id !== 'string' || !id.toString().startsWith('season_')
    );

    monitoringPolygonIds.forEach(polygonId => {
      this.mapInstance!.mapController$.next({removePolygon: {id: polygonId}});
      this.managedPolygonIds.delete(polygonId);
    });
  }

  /**
   * Clear season layers (season markers and polygons)
   */
  public clearSeasonLayers(): void {
    if (!this.mapInstance) return;

    // Remove only season markers (those with 'season_' prefix)
    const seasonMarkerIds = Array.from(this.managedMarkerIds).filter(id =>
      id.toString().startsWith('season_')
    );


    this.mapInstance!.mapController$.next({removeMarkers: seasonMarkerIds});
    seasonMarkerIds.forEach(markerId => {
      this.managedMarkerIds.delete(markerId);
    });


    // Remove only season polygons (those with 'season_' prefix in their ID)
    const seasonPolygonIds = Array.from(this.managedPolygonIds).filter(id =>
      typeof id === 'string' && id.toString().startsWith('season_')
    );



    this.mapInstance!.mapController$.next({removePolygons: seasonPolygonIds as string[]});

    seasonPolygonIds.forEach(polygonId => {
      this.managedPolygonIds.delete(polygonId);
    });
  }

  /**
   * Update monitoring objects display based on zoom level
   */
  private updateMonitoringObjectsDisplay(): void {
    const objects = this.monitoringObjects$.value;
    this.renderDetailedViewMonitoring(objects);
    this.renderClusteredViewMonitoring(objects);

  }

  /**
   * Update seasons display based on zoom level
   */
  private updateSeasonsDisplay(): void {
    const seasons = this.seasons$.value;

    if (this.currentZoom >= this.zoomThreshold) {
      // High zoom: show individual markers and polygons
      this.renderDetailedViewSeasons(seasons);
    } else {
      // Low zoom: use clustering
      this.renderClusteredViewSeasons(seasons);
    }
  }

  /**
   * Render detailed view with markers and polygons (high zoom) - Monitoring
   */
  private renderDetailedViewMonitoring(objects: MapMonitoringObject[]): void {
    if (!this.mapInstance) return;

    objects.forEach(obj => {
      if (obj.pointJson && (obj.polygonJson?.length === 0 || !obj.polygonJson)) {
        // Check if marker is not already added
        const markerId = `monitoring_${obj.id}`;
        if (!this.managedMarkerIds.has(markerId)) {
          // Add marker for point
          this.addMonitoringPointMarker(obj);
        }
      } else if (obj.polygonJson && obj.polygonJson.length > 0) {
        // Check if polygon is not already added
        if (!this.managedPolygonIds.has(obj.id!)) {
          // Draw polygon with center marker
          this.addMonitoringPolygon(obj);
        }
      }
    });
  }

  /**
   * Render clustered view (low zoom) - Monitoring
   */
  private renderClusteredViewMonitoring(objects: MapMonitoringObject[]): void {
    if (!this.mapInstance) return;

    const markers = objects
      .filter(obj => (obj.polygonJson?.length === 0 || !obj.polygonJson) && obj.pointJson)
      .map(obj => this.createMarkerFromMonitoringObject(obj))
      .filter(marker => marker !== null);

    // Track marker IDs
    markers.forEach(marker => this.managedMarkerIds.add(marker.id));

    this.mapInstance.mapController$.next({addMarkers: markers});

    // Also render polygons with their center markers in clustered view
    objects.forEach(obj => {
      if (obj.polygonJson && obj.polygonJson.length > 0) {
        // Check if polygon is not already added
        if (!this.managedPolygonIds.has(obj.id!)) {
          // Draw polygon with center marker
          this.addMonitoringPolygon(obj);
        }
      }
    });
  }

  /**
   * Render detailed view - Seasons
   */
  private renderDetailedViewSeasons(seasons: Season[]): void {
    if (!this.mapInstance) return;

    seasons.forEach(season => {
      if (season.polygonJson) {
        // Check if polygon is not already added
        const polygonId = `season_${season.id}`;
        if (!this.managedPolygonIds.has(polygonId)) {
          // Draw polygon - don't add marker if polygon exists
          this.addSeasonPolygon(season);
        }
      } else if (season.latitude && season.longitude) {
        // Check if marker is not already added
        const markerId = `season_${season.id}`;
        if (!this.managedMarkerIds.has(markerId)) {
          // Add marker for point only if no polygon
          this.addSeasonMarker(season);
        }
      }
    });
  }

  /**
   * Render clustered view - Seasons
   */
  private renderClusteredViewSeasons(seasons: Season[]): void {
    if (!this.mapInstance) return;

    const markers = seasons
      .filter(season => season.latitude && season.longitude)
      .map(season => this.createMarkerFromSeason(season))
      .filter(marker => marker !== null);

    // Track marker IDs
    markers.forEach(marker => this.managedMarkerIds.add(marker.id));

    this.mapInstance.mapController$.next({addMarkers: markers});
  }

  /**
   * Add a marker for a season
   */
  private addSeasonMarker(season: Season): void {
    if (!this.mapInstance || !season.latitude || !season.longitude) return;

    const marker = this.createMarkerFromSeason(season);
    if (marker) {
      this.managedMarkerIds.add(marker.id);
      this.mapInstance.mapController$.next({addMarker: marker});
    }
  }

  private uniqueFieldIds(seasons: Season[]): string[] {
    const ids = new Set<string>();
    for (const season of seasons) {
      if (season.fieldId) {
        ids.add(season.fieldId);
      }
    }
    return [...ids];
  }

  private loadFieldNamesByIds(ids: string[]): Observable<Map<string, string>> {
    if (!ids.length) {
      return of(new Map<string, string>());
    }
    return this.fieldService
      .cropMonitoringFieldGetByQueryPost({
        filters: [
          {
            filterField: 'id',
            filterOperator: FilterOperator.AnyOf,
            filterValue: ids,
          },
        ],
        take: ids.length,
        offset: 0,
      } as any)
      .pipe(
        map((fields: Field[]) => {
          const names = new Map<string, string>();
          for (const field of fields ?? []) {
            if (field.id && field.name != null && String(field.name).trim().length > 0) {
              names.set(field.id, String(field.name).trim());
            }
          }
          return names;
        }),
        catchError(() => of(new Map<string, string>())),
      );
  }

  private getSeasonDisplayTitle(season: Season): string {
    const fieldName = season.fieldId ? this.fieldNameById.get(season.fieldId) : undefined;
    const cropId = season.cropId ?? (season as {plannedCropId?: string}).plannedCropId;
    const cropName = cropId
      ? this.cropCacheService.getCropNameById(String(cropId), true) ?? this.cropNameMap.get(String(cropId))
      : '';
    const name = formatSeasonName({
      cropName,
      plantingDate: season.plantingDate,
      plantingDatePlan: season.plantingDatePlan,
      isPlanned: season.isPlanned === true,
      fieldName,
      kind: seasonNameKindFrom(season),
    });
    if (name) {
      return name;
    }
    return season.title || this.translate.instant('SEASON');
  }

  private cropDisplayName(cropId: string | null | undefined): string {
    if (cropId == null || String(cropId).length === 0) {
      return escapePopupHtml('—');
    }
    const name =
      this.cropCacheService.getCropNameById(String(cropId), true) ??
      this.cropNameMap.get(String(cropId)) ??
      String(cropId);
    return escapePopupHtml(name);
  }

  private formatPopupAreaHa(area: unknown): string {
    if (area == null || area === '') {
      return escapePopupHtml('—');
    }
    const n = Number(area);
    if (!Number.isFinite(n)) {
      return escapePopupHtml(String(area));
    }
    return escapePopupHtml(n.toFixed(2));
  }

  private buildSeasonPopupHtml(season: Season): string {
    const title = escapePopupHtml(this.getSeasonDisplayTitle(season));
    const haUnit = escapePopupHtml(this.translate.instant('HA'));
    return (
      `<b>${title}</b>` +
      `<br><small>${escapePopupHtml(this.translate.instant('CROP_ID'))}: ${this.cropDisplayName(season.cropId)}</small>` +
      `<br><small>${escapePopupHtml(this.translate.instant('FIELD_AREA'))}: ${this.formatPopupAreaHa(season.fieldArea)} ${haUnit}</small>`
    );
  }

  /**
   * Create marker from season
   */
  private createMarkerFromSeason(season: Season): any {
    if (!season.latitude || !season.longitude) return null;

    const cropColor = this.getCropColor(season.cropId!);
    const title = this.getSeasonDisplayTitle(season);

    return {
      id: `season_${season.id}`,
      lat: season.latitude,
      lng: season.longitude,
      title,
      popup: this.buildSeasonPopupHtml(season),
      openPopupOnHover: true,
      data: {
        seasonId: season.id,
        cropId: season.cropId,
        fieldId: season.fieldId,
        startDate: season.startDate,
        endDate: season.endDate,
        fieldArea: season.fieldArea
      },
      icon: 'marker',
      color: cropColor,
      onClick: (marker: any) => {
        this.onSeasonMarkerClick(marker);
      }
    };
  }

  /**
   * Add a polygon for a season
   */
  private addSeasonPolygon(season: Season): void {
    if (!this.mapInstance || !season.polygonJson) return;

    const coordinates = PolygonConverterUtil.polygonJsonToCoordinates(season.polygonJson);

    if (!PolygonConverterUtil.isValidPolygon(coordinates)) {
      return;
    }

    // Get the color based on crop name
    const cropColor = this.getCropColor(season.cropId!);

    // Use consistent ID format with 'season_' prefix
    const polygonId = `season_${season.id}`;

    // Track polygon ID
    this.managedPolygonIds.add(polygonId);

    this.mapInstance.mapController$.next({
      addPolygon: {
        coordinates: coordinates,
        properties: {
          id: polygonId,
          name: this.getSeasonDisplayTitle(season),
          popup: this.buildSeasonPopupHtml(season),
          openPopupOnHover: true,
          strokeColor: cropColor,
          strokeWidth: 2,
          fillColor: cropColor,
          fillOpacity: 0.1,
          fill: true,
          data: {
            seasonId: season.id,
            cropId: season.cropId,
            cropName: this.cropCacheService.getCropNameById(season.cropId!, true)
              ?? this.cropNameMap.get(season.cropId!)
              ?? 'Unknown',
            fieldId: season.fieldId,
            startDate: season.startDate,
            endDate: season.endDate,
            fieldArea: season.fieldArea,
            plantingDate: season.plantingDate,
            harvestingDate: season.harvestingDate
          },
          onClick: (polygon: any) => {
            this.onSeasonPolygonClick(polygon);
          }
        }
      }
    });
  }

  /**
   * Get color for a crop by ID
   */
  private getCropColor(cropId: string): string {
    return this.cropCacheService.getCropColorById(cropId);
  }

  /**
   * Handle season polygon click
   */
  private onSeasonPolygonClick(polygon: any): void {
    // Calculate center of polygon for weather details
    if (polygon.data && polygon.coordinates && polygon.coordinates.length > 0) {
      const center = PolygonConverterUtil.getPolygonCenter(polygon.coordinates);
      this.polygonClick$.next({
        lat: center?.lat as number,
        lng: center?.lng as number,
        data: polygon.data
      });
    }
  }

  /**
   * Handle polygon click
   */
  private onPolygonClick(polygon: any): void {
    // Calculate center of polygon for weather details
    if (polygon.data && polygon.coordinates && polygon.coordinates.length > 0) {
      const center = PolygonConverterUtil.getPolygonCenter(polygon.coordinates);
      this.polygonClick$.next({
        lat: center?.lat as number,
        lng: center?.lng as number,
        data: polygon.data
      });
    }
  }

  /**
   * Create marker from monitoring object
   */
  private createMarkerFromMonitoringObject(obj: MapMonitoringObject): any {
    if (!obj.pointJson || !obj.pointJson.lat || !obj.pointJson.lon) return null;

    return {
      id: `monitoring_${obj.id}`,
      lat: obj.pointJson.lat,
      lng: obj.pointJson.lon,
      title: obj.name || this.translate.instant('MONITORING_POINT'),
      popup: `<b>${obj.name || this.translate.instant('MONITORING_POINT')}</b>
                <br>
                <small>
                ${obj.pointJson.lat.toFixed(4)},  ${obj.pointJson.lon.toFixed(4)}
              </small>
            `,
      data: {
        monitoringObjectId: obj.id,
        ...obj
      },
      customIcon: this.createCustomMarkerIcon(),
      onClick: (marker: any) => {
        this.onMarkerClick(marker);
      }
    };
  }

  /**
   * Create custom marker icon with white circle and pointer
   */
  private createCustomMarkerIcon(): string {
    return `
      <div style="
        width: 20px;
        height: 30px;
        position: relative;
      ">
        <svg width="20" height="30" viewBox="0 0 32 45" xmlns="http://www.w3.org/2000/svg">
          <!-- Shadow -->
          <ellipse cx="16" cy="42" rx="8" ry="3" fill="rgba(0,0,0,0.2)" />

          <!-- Main marker shape -->
          <path d="M16 0 C7.2 0 0 7.2 0 16 C0 20 2 24 4 27 L16 45 L28 27 C30 24 32 20 32 16 C32 7.2 24.8 0 16 0 Z"
                fill="white"
                stroke="#9a9a9a"
                stroke-width="2"/>
        </svg>
      </div>
    `;
  }

  /**
   * Add a marker for a monitoring point
   */
  private addMonitoringPointMarker(obj: MapMonitoringObject): void {
    if (!this.mapInstance || !obj.pointJson) return;

    const marker = this.createMarkerFromMonitoringObject(obj);
    if (marker) {
      this.managedMarkerIds.add(marker.id);
      this.mapInstance.mapController$.next({addMarker: marker});
    }
  }

  /**
   * Add a polygon for a monitoring object
   */
  private addMonitoringPolygon(obj: MapMonitoringObject): void {
    if (!this.mapInstance || !obj.polygonJson || obj.polygonJson.length === 0) return;

    // Map coordinates from {lon, lat} format to [lat, lon] tuple format
    const coordinates: [number, number][] = obj.polygonJson.map(coord => [
      coord.lat || 0,
      coord.lon || 0
    ]);

    // Validate that we have at least 3 points for a polygon
    if (coordinates.length < 3) {
      return;
    }

    // Calculate center of polygon for popup coordinates and marker
    const center = PolygonConverterUtil.getPolygonCenter(coordinates);

    // Track polygon ID
    this.managedPolygonIds.add(obj.id!);

    // Add the polygon to the map
    this.mapInstance.mapController$.next({
      addPolygon: {
        coordinates: coordinates,
        properties: {
          id: obj.id,
          name: obj.name,
          strokeColor: '#2196F3',
          strokeWidth: 2,
          fillColor: '#2196F3',
          fillOpacity: 0.2,
          fill: true,
          popup: `<b>${obj.name || this.translate.instant('MONITORING_POINT')}</b>
                <br>
                <small>
                ${center?.lat.toFixed(4)},  ${center?.lng.toFixed(4)}
              </small>
            `,
          data: {
            monitoringObjectId: obj.id,
            ...obj,
          },
          onClick: (polygon: any) => {
            this.onPolygonClick(polygon);
          }
        }
      }
    });

    // Add a marker at the center of the polygon
    const centerMarkerId = `monitoring_center_${obj.id}`;

    // Check if marker is not already added
    if (!this.managedMarkerIds.has(centerMarkerId)) {
      const centerMarker = {
        id: centerMarkerId,
        lat: center?.lat as number,
        lng: center?.lng as number,
        title: obj.name || this.translate.instant('MONITORING_POINT'),
        popup: `<b>${obj.name || this.translate.instant('MONITORING_POINT')}</b>
                <br>
                <small>
                ${center?.lat.toFixed(4)},  ${center?.lng.toFixed(4)}
              </small>
            `,
        data: {
          monitoringObjectId: obj.id,
          isCenterMarker: true,
          ...obj
        },
        customIcon: this.createCustomMarkerIcon(),
        onClick: (marker: any) => {
          this.onMarkerClick(marker);
        },
        zIndex: 1000,
        interactive: true,
        pane: 'markerPane'
      };

      this.managedMarkerIds.add(centerMarkerId);
      this.mapInstance.mapController$.next({addMarker: centerMarker});
    }
  }

  /**
   * Update a monitoring polygon with new coordinates and/or properties
   * If the polygon doesn't exist, it will be added automatically
   * Also updates the monitoring object in the monitoringObjects$ BehaviorSubject
   * @param obj - The updated MapMonitoringObject
   */
  public updateMonitoringPolygon(obj: MapMonitoringObject): void {

    if (!this.mapInstance || !obj.polygonJson || obj.polygonJson.length === 0) {
      return;
    }

    // Map coordinates from {lon, lat} format to [lat, lon] tuple format
    const coordinates: [number, number][] = obj.polygonJson.map(coord => [
      coord.lat || 0,
      coord.lon || 0
    ]);

    // Validate that we have at least 3 points for a polygon
    if (coordinates.length < 3) {
      return;
    }

    // Calculate new center of polygon
    const center = PolygonConverterUtil.getPolygonCenter(coordinates);

    // Check if polygon exists
    const polygonExists = this.managedPolygonIds.has(obj.id!);

    if (polygonExists) {
      // Update existing polygon
      this.mapInstance.mapController$.next({
        updatePolygon: {
          id: obj.id as string,
          coordinates: coordinates,
          properties: {
            id: obj.id,
            name: obj.name,
            strokeColor: '#2196F3',
            strokeWidth: 2,
            fillColor: '#2196F3',
            fillOpacity: 0.2,
            fill: true,
            popup: `<b>${obj.name || this.translate.instant('MONITORING_POINT')}</b>
                <br>
                <small>
                ${center?.lat.toFixed(4)},  ${center?.lng.toFixed(4)}
              </small>
            `,
            data: {
              monitoringObjectId: obj.id,
              ...obj,
            },
            onClick: (polygon: any) => {
              this.onPolygonClick(polygon);
            }
          }
        }
      });
    } else {
      // Polygon doesn't exist, add it using the existing addMonitoringPolygon method
      this.addMonitoringPolygon(obj);
    }

    // Update monitoringObjects$ BehaviorSubject
    const currentObjects = this.monitoringObjects$.value;
    const objectIndex = currentObjects.findIndex(o => o.id === obj.id);

    if (objectIndex !== -1) {
      // Update existing object
      const updatedObjects = [...currentObjects];
      updatedObjects[objectIndex] = obj;
      this.monitoringObjects$.next(updatedObjects);
    } else {
      // Add new object
      this.monitoringObjects$.next([...currentObjects, obj]);
    }
  }


  /**
   * Handle marker click
   */
  private onMarkerClick(marker: any): void {
    this.markerClick$.next({
      lat: marker.lat,
      lng: marker.lng,
      data: marker.data
    });
  }

  /**
   * Handle season marker click
   */
  private onSeasonMarkerClick(marker: any): void {
    this.markerClick$.next({
      lat: marker.lat,
      lng: marker.lng,
      data: marker.data
    });
  }

}
